import {
  Card,
  FormField,
  IconWrapper,
  Radio,
  TextInput,
} from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { Controller, useWatch } from "react-hook-form";

import { CircleCheck, User02 } from "@icons";
import type { TechRequestDataFormContact } from "@services/apiEcms/apiEcms.types";
import { emailRegex, phoneNumberRegex } from "@utils/form";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./TechnicalContactCard.module.css";
import type { TechnicalContactCardProps } from "./TechnicalContactCard.types";
import type { AddPodFormData } from "../AddPodForm/AddPodForm.types";
import { getIsFilled } from "../AddPodForm/AddPodForm.utils";
import formClasses from "../form.module.css";

const technicalContactFields: Array<keyof AddPodFormData> = [
  "civility",
  "firstName",
  "lastName",
  "phone",
  "email",
];

const CIVILITIES: Array<TechRequestDataFormContact["civility"]> = ["Mr", "Mrs"];

export const TechnicalContactCard: FC<TechnicalContactCardProps> = ({
  errors,
  control,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const cardId = useId();

  const [civility, firstName, lastName, phone, email] = useWatch({
    control,
    name: ["civility", "firstName", "lastName", "phone", "email"],
  });

  const isFilled = getIsFilled(technicalContactFields, errors, {
    civility,
    firstName,
    lastName,
    phone,
    email,
  });

  return (
    <Card aria-labelledby={cardId} as="section" className={formClasses.card}>
      <h3
        className={classNames(formClasses.titleContainer, "font-title-md")}
        id={cardId}
      >
        {isFilled ? (
          <IconWrapper
            className={formClasses.successIcon}
            role="presentation"
            size="lg"
            src={CircleCheck}
          />
        ) : (
          <IconWrapper role="presentation" size="md" src={User02} />
        )}
        {t("addPodDrawer.contact.title")}
      </h3>
      <div className={classes.formContainer}>
        <Controller
          control={control}
          name="civility"
          render={({ field, fieldState }) => (
            <FormField
              as="div"
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
            >
              <div className={classes.inputContainer}>
                {CIVILITIES.map((option) => (
                  <Radio
                    key={option}
                    {...field}
                    checked={field.value === option}
                    hasError={Boolean(fieldState.error)}
                    label={t(`addPodDrawer.contact.fields.civility${option}`)}
                    onChange={(event) => field.onChange(event.target.value)}
                    value={option}
                  />
                ))}
              </div>
            </FormField>
          )}
          rules={{ required: "form.error.required" }}
        />
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.contact.fields.firstname")}
                size="lg"
              />
            )}
            rules={{ required: "form.error.required" }}
          />

          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.contact.fields.lastname")}
                size="lg"
              />
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.contact.fields.phoneNumber")}
                size="lg"
              />
            )}
            rules={{
              required: "form.error.required",
              pattern: {
                value: phoneNumberRegex,
                message: "form.error.invalidFormat",
              },
            }}
          />

          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.contact.fields.email")}
                size="lg"
              />
            )}
            rules={{
              required: "form.error.required",
              pattern: {
                value: emailRegex,
                message: "form.error.invalidFormat",
              },
            }}
          />
        </div>
      </div>
    </Card>
  );
};
